<template>
    <div class="container_view">
        <v-card class="content_card" elevation="10" variant="flat">
            <div>
                <header>
                    <v-breadcrumbs
                        :items="[
                            { title: 'Base de Datos 2.0', to: '/SGCCV/database/imports' },
                            { title: 'Carga SCL', disabled: true },
                        ]"
                        class="pb-0"
                    ></v-breadcrumbs>
                    <v-card-title> Carga SCL </v-card-title>
                    <v-divider class="mx-3"></v-divider>
                </header>
            </div>

            <div class="content_main" :class="{ mapping_content: step === 'mapping' }">
                <div v-if="step !== 'queued'" class="steps_header">
                    <div
                        v-for="(item, index) in steps"
                        :key="item.key"
                        class="step_item"
                        :class="{ active: step === item.key, done: stepIndex > index }"
                    >
                        <v-icon size="20" class="mr-1">{{ index < stepIndex ? 'mdi-check-circle' : `mdi-numeric-${index + 1}-circle` }}</v-icon>
                        <span>{{ item.label }}</span>
                    </div>
                </div>

                <div class="step_body" :class="{ mapping_workspace: step === 'mapping' }">
                    <ImportUploadStep
                        v-if="step === 'upload'"
                        :loading="loading"
                        @file-selected="onFileSelected"
                    />

                    <ImportMappingStep
                        v-else-if="step === 'mapping'"
                        :mapping="mapping"
                        :configured-fields="configuredFields"
                        :providedColumns="providedColumns"
                        :sampleRow="sampleRows[0] || []"
                        :loading="loading"
                        @update="onMappingUpdate"
                        @continue="step = 'confirm'"
                        @back="step = 'upload'"
                    />

                    <ImportConfirmStep
                        v-else-if="step === 'confirm'"
                        :filename="originalFilename"
                        :mapping="mapping"
                        :providedColumns="providedColumns"
                        :samples="sampleRows"
                        :loading="loading"
                        @confirm="onConfirm"
                        @back="step = 'mapping'"
                    />
                    <div v-else class="queued_success">
                        <div class="text-h6">{{ importState ? statusInfo(importState.status).label : 'Iniciando carga' }}</div>
                        <p class="text-body-2 mt-2">{{ originalFilename }}</p>
                        <p class="text-body-2">La carga continúa en segundo plano. Puedes salir de esta pantalla y seguir trabajando; consulta el avance en Base de Datos 2.0.</p>
                        <v-progress-linear v-if="importState" :model-value="importState.progress" color="indigo-darken-3" class="mb-3" />
                        <div v-for="stage in progressStages" :key="stage.event" class="text-body-2 py-1">
                            <v-icon :color="completedEvents.includes(stage.event) ? 'green' : 'grey'" size="18" class="mr-2">
                                {{ completedEvents.includes(stage.event) ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                            </v-icon>{{ stage.label }}
                        </div>
                        <v-alert v-if="importState?.failureMessage" type="error" variant="tonal" class="mt-3">{{ importState.failureMessage }}</v-alert>
                        <div class="d-flex justify-center mt-4">
                            <v-btn variant="flat" color="indigo-darken-3" text="Ver seguimiento" @click="goToTracking"></v-btn>
                        </div>
                    </div>
                </div>
            </div>
        </v-card>

        <v-dialog v-model="leaveDialog" persistent max-width="420px">
            <v-card>
                <v-card-title>¿Salir de la carga?</v-card-title>
                <v-card-text>El progreso de esta carga se perderá.</v-card-text>
                <v-card-actions class="justify-end">
                    <v-btn variant="text" text="Continuar carga" @click="cancelLeave"></v-btn>
                    <v-btn color="indigo-darken-3" variant="flat" text="Salir" @click="confirmLeave"></v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <DialogComponent :value="errorDialog" :size="500">
            <v-card-title>
                <span class="text-h6">Ocurrió un error</span>
            </v-card-title>
            <v-card-text>
                <div class="text-body-2">{{ errorMessage }}</div>
            </v-card-text>
            <v-card-actions class="d-flex justify-end">
                <v-btn variant="flat" color="indigo-darken-3" text="Cerrar" @click="errorDialog = false"></v-btn>
            </v-card-actions>
        </DialogComponent>
        <SnackbarComponent :value="snackbarData" />
    </div>
</template>

<script>
    import { DatabaseService } from '@/services/DatabaseService';
    import DialogComponent from '@/components/Dialog.vue';
    import ImportUploadStep from './components/ImportUploadStep.vue';
    import ImportMappingStep from './components/ImportMappingStep.vue';
    import ImportConfirmStep from './components/ImportConfirmStep.vue';
    import { useDebtorFieldsStore } from '@/stores/debtorFields';
    import { SCL_DEBTOR_FIELDS, SCL_SECTIONS } from './sclDebtorFields';
    import { statusInfo, isTerminal } from './importStatus';

    export default {
        name: 'SCLImportView',
        components: {
            DialogComponent,
            ImportUploadStep,
            ImportMappingStep,
            ImportConfirmStep,
        },
        data () {
            return {
                steps: [
                    { key: 'upload', label: 'Subir' },
                    { key: 'mapping', label: 'Mapear' },
                    { key: 'confirm', label: 'Confirmar' },
                ],
                step: 'upload',
                fileAttached: false,
                leaveDialog: false,
                pendingNavigation: null,
                loading: false,
                importId: null,
                debtorTabs: {},
                importState: null,
                completedEvents: [],
                pollTimer: null,
                progressStages: [
                    { event: 'file_uploaded', label: 'Archivo guardado' },
                    { event: 'job_queued', label: 'Trabajo encolado' },
                    { event: 'processing_started', label: 'Procesamiento iniciado' },
                    { event: 'temporary_files_saved', label: 'Seis archivos temporales guardados' },
                    { event: 'temporary_tables_loaded', label: 'Tablas temporales cargadas' },
                    { event: 'data_copied', label: 'Datos copiados a tablas finales' },
                    { event: 'processing_completed', label: 'Carga completada' },
                ],
                originalFilename: '',
                providedColumns: [],
                mapping: [],
                sampleRows: [],
                errorDialog: false,
                errorMessage: '',
                snackbarData: {
                    openSnackbar: false,
                    description: '',
                },
            };
        },
        computed: {
            configuredFields () {
                const allowed = new Set(this.mapping.map(field => field.key));
                const result = {};
                SCL_SECTIONS.forEach(section => {
                    result[section.key] = (this.debtorTabs[section.key] || []).map(field => {
                        // Profiles normally expose the SCL key directly. Older
                        // configurations can still use debtor-information keys.
                        const key = allowed.has(field.key)
                            ? field.key
                            : SCL_DEBTOR_FIELDS[`${section.group}:${field.key}`];
                        if (!key || !allowed.has(key)) return null;
                        return { key, label: field.name };
                    }).filter(Boolean);
                });
                return result;
            },
            stepIndex () {
                return this.steps.findIndex((item) => item.key === this.step);
            },
        },
        beforeUnmount () {
            this.stopPolling();
        },
        beforeRouteLeave (to, from, next) {
            if (this.step === 'queued' || (!this.fileAttached && this.step === 'upload')) {
                next();
                return;
            }
            this.pendingNavigation = next;
            this.leaveDialog = true;
        },
        methods: {
            statusInfo,
            async loadDebtorConfig () {
                const store = useDebtorFieldsStore();
                try {
                    if (!store.loaded) await store.cargar();
                    this.debtorTabs = store.tabs || {};
                } catch (_) {
                    this.debtorTabs = {};
                }
            },
            startPolling () {
                this.stopPolling();
                this.loadProgress();
                this.pollTimer = setInterval(() => this.loadProgress(), 3000);
            },
            stopPolling () {
                if (this.pollTimer) clearInterval(this.pollTimer);
                this.pollTimer = null;
            },
            async loadProgress () {
                if (!this.importId) return;
                try {
                    const [state, logs] = await Promise.all([
                        DatabaseService.getImport(this.importId),
                        DatabaseService.getImportLogs(this.importId),
                    ]);
                    if (!state.error) {
                        this.importState = state.response;
                        if (isTerminal(this.importState.status)) this.stopPolling();
                    }
                    if (!logs.error) this.completedEvents = (logs.response || []).map(log => log.event);
                } catch (_) { /* A later poll can recover a transient connection failure. */ }
            },
            goToTracking () {
                this.$router.push('/SGCCV/database/imports');
            },
            cancelLeave () {
                this.leaveDialog = false;
                const next = this.pendingNavigation;
                this.pendingNavigation = null;
                if (next) next(false);
            },
            confirmLeave () {
                this.leaveDialog = false;
                const next = this.pendingNavigation;
                this.pendingNavigation = null;
                if (next) next();
            },
            onFileSelected (file) {
                this.fileAttached = true;
                this.loading = true;

                const formData = new FormData();
                formData.append('archivo', file);
                formData.append('import_type', 'scl');

                DatabaseService.preview(formData)
                    .then((resp) => {
                        this.loading = false;

                        if (resp.error) {
                            this.showError(resp.msg);
                            return;
                        }

                        this.applyPreview(resp.response);
                        this.loadDebtorConfig();
                        this.step = 'mapping';
                    })
                    .catch(() => {
                        this.loading = false;
                        this.showError('No fue posible previsualizar el archivo.');
                    });
            },
            applyPreview (preview) {
                this.importId = preview.importId;
                this.originalFilename = preview.originalFilename;
                this.providedColumns = preview.providedColumns || [];
                this.mapping = (preview.mapping || []).map((field) => ({ ...field }));
                this.sampleRows = preview.sampleRows || [];
            },
            onMappingUpdate ({ key, providedIndex }) {
                const field = this.mapping.find((item) => item.key === key);
                if (!field) {
                    return;
                }

                field.providedIndex = providedIndex;
                field.status = providedIndex === null || providedIndex === undefined
                    ? (field.required ? 'missing' : 'optional')
                    : 'matched';
            },
            onConfirm () {
                this.loading = true;

                const payload = {
                    import_id: this.importId,
                    mapping: this.buildMappingPayload(),
                };

                DatabaseService.confirmImport(payload)
                    .then((resp) => {
                        this.loading = false;

                        if (resp.error) {
                            this.showError(resp.msg);
                            return;
                        }

                        this.snackbarData.description = 'Importación confirmada.';
                        this.snackbarData.openSnackbar = true;
                        this.step = 'queued';
                        this.startPolling();
                    })
                    .catch(() => {
                        this.loading = false;
                        this.showError('No fue posible confirmar la importación.');
                    });
            },
            buildMappingPayload () {
                const payload = {};
                this.mapping.forEach((field) => {
                    if (field.providedIndex !== null && field.providedIndex !== undefined) {
                        payload[field.key] = field.providedIndex;
                    }
                });
                return payload;
            },
            showError (message) {
                this.errorMessage = typeof message === 'string' ? message : JSON.stringify(message);
                this.errorDialog = true;
            },
        },
    };
</script>

<style scoped>
    .content_card {
        display: flex;
        flex-direction: column;
        min-height: 0;
    }

    .content_main {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        padding: 16px;
        overflow: auto;
    }

    .content_main.mapping_content {
        overflow: hidden;
    }

    .steps_header {
        display: flex;
        gap: 24px;
        margin-bottom: 24px;
        border-bottom: 1px solid #e0e0e0;
        padding-bottom: 12px;
    }

    .step_item {
        display: flex;
        align-items: center;
        color: #9e9e9e;
        font-weight: 500;
    }

    .step_item.active {
        color: #1a237e;
    }

    .step_item.done {
        color: #4caf50;
    }

    .step_body {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        min-width: 0;
        width: 100%;
        max-width: 960px;
        margin: 0 auto;
    }

    .step_body.mapping_workspace {
        max-width: 1180px;
    }

    .queued_success {
        padding: 40px 16px;
    }
</style>
